import React, { useEffect, useState, useRef, useMemo } from "react";
import { Typography, ConfigProvider, Drawer } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { fetchManagers, deleteMerchant } from "../../redux/MerchantsUser";
import { useTheme } from "../../context/ThemeContext";
import { getAntdThemeConfig } from "../../utils/antdTheme";
import { ThemeProvider } from "styled-components";
import MerchantBox from "./MerchantBox";
import MerchantsTable from "./MerchantsTable";
import CustomInput from "../../commonComponents/input";
import { Merchant } from "./types";
import notification from "../../commonComponents/Notification/Notification";
import ConfirmDialog from "../../commonComponents/ConfirmDialog";
import {
  Container,
  StyledCard,
  HeaderSection,
  TopControlBar,
  AddButton,
  DrawerContentWrapper,
} from "./styles";

const { Title } = Typography;

const MerchantManager: React.FC<{ role?: string }> = ({ role }) => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentMerchant, setCurrentMerchant] = useState<Merchant | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [merchantToDelete, setMerchantToDelete] = useState<Merchant | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [currentManagerId, setCurrentManagerId] = useState<string | null>(null);

  const dispatch = useDispatch<AppDispatch>();
  const { managers, loading } = useSelector(
    (state: RootState) => state.merchants
  );
  const { merchantData } = useSelector(
    (state: RootState) => state.merchantDashboard
  );

  const loggedInMerchantId = merchantData?.id ?? undefined;
  const hasFetchedRef = useRef(false);
  const filteredMerchants = useMemo(() => {
    if (!Array.isArray(managers)) return [];
    if (!searchQuery) return managers;
    const query = searchQuery.toLowerCase();
    return managers.filter(
      (merchant) =>
        merchant?.name?.toLowerCase().includes(query) ||
        merchant?.username?.toLowerCase().includes(query) ||
        merchant?.email?.toLowerCase().includes(query)
    );
  }, [managers, searchQuery]);

  const paginatedMerchants = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredMerchants.slice(startIndex, endIndex);
  }, [filteredMerchants, currentPage, pageSize]);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      hasFetchedRef.current = true;
      dispatch(fetchManagers());
    }
  }, [dispatch]);

  const handleAddClick = () => {
    setCurrentMerchant(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (merchant: Merchant) => {
    setCurrentMerchant(merchant);
    setIsDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
    setCurrentMerchant(null);
  };

  const handleDeleteClick = (merchant: Merchant) => {
    setMerchantToDelete(merchant);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!merchantToDelete) return;
    
    setIsDeleting(true);
    const result = await dispatch(
      deleteMerchant({ type: "manager", id: merchantToDelete.id })
    );
    setIsDeleting(false);
    
    if (deleteMerchant.fulfilled.match(result)) {
      notification.success("Merchant manager deleted successfully");
      setIsDeleteModalOpen(false);
      setMerchantToDelete(null);
      dispatch(fetchManagers());
    } else {
      const errorMsg =
        typeof result.payload === "string"
          ? result.payload
          : "Error deleting merchant manager";
      notification.error("Delete Failed", errorMsg);
    }
  };

  const handleDeleteCancel = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false);
      setMerchantToDelete(null);
    }
  };

  const handleAddStaff = (managerId: string) => {
    setCurrentManagerId(managerId);
    setIsStaffModalOpen(true);
  };

  const handleStaffModalClose = () => {
    setIsStaffModalOpen(false);
    setCurrentManagerId(null);
  };

  const handleStaffSaved = () => {
    setIsStaffModalOpen(false);
    setCurrentManagerId(null);
    dispatch(fetchManagers());
  };

  if (role !== "merchant") {
    return (
      <Container>
        <StyledCard>
          <div>You don't have the permissions to access this page.</div>
        </StyledCard>
      </Container>
    );
  }

  return (
    <ThemeProvider theme={{ mode: isDarkMode ? "dark" : "light" }}>
      <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
        <Container>
          <HeaderSection>
            <Title level={2} style={{ margin: 0 }}>
              Manager
            </Title>
            <AddButton
              type="primary"
              icon={<FontAwesomeIcon icon={faPlus} />}
              onClick={handleAddClick}
              size="large"
            >
              Add New Manager
            </AddButton>
          </HeaderSection>

          <StyledCard>
            <TopControlBar>
              <CustomInput
                placeholder="Search managers by name..."
                value={searchQuery}
                onChange={(value: string) => setSearchQuery(value)}
                allowClear
                width="300px"
              />
            </TopControlBar>

            <MerchantsTable
              merchants={paginatedMerchants}
              currentPage={currentPage}
              pageSize={pageSize}
              total={filteredMerchants.length}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              onEdit={handleEdit}
              onDelete={handleDeleteClick}
              loading={loading.managers}
              isMerchantUserView={true}
              onAddStaff={handleAddStaff}
            />
          </StyledCard>

          <Drawer
            title={currentMerchant ? "Edit Manager" : "Add New Manager"}
            placement="right"
            onClose={handleDrawerClose}
            open={isDrawerOpen}
            width={600}
            destroyOnClose
            styles={{
              body: {
                padding: 0,
                background: isDarkMode ? "#1a202c" : "#ffffff",
              },
              header: {
                background: isDarkMode ? "#1a202c" : "#ffffff",
                borderBottom: `1px solid ${isDarkMode ? "#434343" : "#f0f0f0"}`,
              },
            }}
          >
            <DrawerContentWrapper>
              <MerchantBox
                initialMerchant={currentMerchant}
                onSave={() => {
                  setIsDrawerOpen(false);
                  setCurrentMerchant(null);
                  dispatch(fetchManagers());
                }}
                onCancel={handleDrawerClose}
                fetchMerchants={() => dispatch(fetchManagers())}
                isManagerMode={true}
                userRoleType={currentMerchant ? undefined : "merchant_manager"}
                merchantId={currentMerchant ? undefined : loggedInMerchantId}
              />
            </DrawerContentWrapper>
          </Drawer>

          <Drawer
            title="Add Staff"
            placement="right"
            onClose={handleStaffModalClose}
            open={isStaffModalOpen}
            width={600}
            destroyOnClose
            styles={{
              body: {
                padding: 0,
                background: isDarkMode ? "#1a202c" : "#ffffff",
              },
              header: {
                background: isDarkMode ? "#1a202c" : "#ffffff",
                borderBottom: `1px solid ${isDarkMode ? "#434343" : "#f0f0f0"}`,
              },
            }}
          >
            <DrawerContentWrapper>
              <MerchantBox
                initialMerchant={null}
                onSave={handleStaffSaved}
                onCancel={handleStaffModalClose}
                fetchMerchants={() => dispatch(fetchManagers())}
                isStaffMode={true}
                managerId={currentManagerId || undefined}
              />
            </DrawerContentWrapper>
          </Drawer>

          <ConfirmDialog
            isOpen={isDeleteModalOpen}
            onClose={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            title="Delete Manager"
            message={
              merchantToDelete
                ? `Are you sure you want to delete "${merchantToDelete.name || merchantToDelete.email || merchantToDelete.username}"? This action cannot be undone.`
                : "Are you sure you want to delete this manager? This action cannot be undone."
            }
            confirmText="Delete"
            cancelText="Cancel"
            confirmColorScheme="red"
            loading={isDeleting}
          />
        </Container>
      </ConfigProvider>
    </ThemeProvider>
  );
};

export default MerchantManager;